import { FlexBox } from "components/Common/FlexBox"
import {
  TutorialWrapper,
  FeatureCard,
  CardContent,
  CardTitle,
  CardDescription,
  CardImageContainer,
  InnerImageContent,
  CardImage,
  PageSubtitle,
  BottomCTA,
  FeatureCards,
  BetaTag,
} from "./styled"
import { useNavigate } from "react-router-dom"
import { BackButton } from "components/Common/BackButton"
import { PageTitle } from "components/Common/PageTitle"
import { useMealTypeQueryParams } from "../hooks"
import PATHS from "router/paths"
import Border from "./Border"

// Multi-resolution images for responsive loading
import tutorialMeal1x from "assets/images/tutorial/tutorial-meal@1x.webp"
import tutorialMeal2x from "assets/images/tutorial/tutorial-meal@2x.webp"
import tutorialMeal3x from "assets/images/tutorial/tutorial-meal@3x.webp"

import tutorialReceipts1x from "assets/images/tutorial/tutorial-receipts@1x.webp"
import tutorialReceipts2x from "assets/images/tutorial/tutorial-receipts@2x.webp"
import tutorialReceipts3x from "assets/images/tutorial/tutorial-receipts@3x.webp"

import tutorialPackage1x from "assets/images/tutorial/tutorial-package@1x.webp"
import tutorialPackage2x from "assets/images/tutorial/tutorial-package@2x.webp"
import tutorialPackage3x from "assets/images/tutorial/tutorial-package@3x.webp"

import tutorialNutritionFact1x from "assets/images/tutorial/tutorial-nutrition-fact@1x.webp"
import tutorialNutritionFact2x from "assets/images/tutorial/tutorial-nutrition-fact@2x.webp"
import tutorialNutritionFact3x from "assets/images/tutorial/tutorial-nutrition-fact@3x.webp"

import Button from "components/Buttons/Button"
import ArrowLeft from "components/Icons/ArrowLeft"
import {
  useIsTutorialShowed,
  useTutorialBackPath,
} from "features/onboarding/hooks"
import WhiteCloseIcon from "components/Icons/WhiteCloseIcon"
import { useSetPageTitle } from "libs/ga"
import BottomBlurCircle from "./BottomBlurCricle"
import TopBlurCricle from "./TopBlurCricle"

interface FeatureImages {
  x1: string
  x2: string
  x3: string
}

const FeatureCardItem = ({
  title,
  description,
  images,
  contentWidth,
  isBeta = false,
}: {
  title: string
  description: string
  images: FeatureImages
  contentWidth: number
  isBeta?: boolean
}) => {
  // Build srcSet for responsive image loading based on device pixel ratio
  const srcSet = `${images.x1} 1x, ${images.x2} 2x, ${images.x3} 3x`

  return (
    <FeatureCard>
      <TopBlurCricle />
      <BottomBlurCircle />
      <CardContent style={{ width: contentWidth }}>
        <FlexBox $alignItems="center" $gap={8}>
          <CardTitle>{title}</CardTitle>
          {isBeta && <BetaTag>Beta</BetaTag>}
        </FlexBox>

        <CardDescription>{description}</CardDescription>
      </CardContent>
      <CardImageContainer>
        <InnerImageContent>
          <CardImage src={images.x1} srcSet={srcSet} alt={title} />
        </InnerImageContent>
        <Border />
      </CardImageContainer>
    </FeatureCard>
  )
}

const features = [
  {
    title: "Meal Photo",
    description:
      "Snap your meal — we'll track calories and nutrients instantly.",
    images: {
      x1: tutorialMeal1x,
      x2: tutorialMeal2x,
      x3: tutorialMeal3x,
    },
    contentWidth: 189,
  },
  {
    title: "Receipts",
    description:
      "Snap a receipt — we'll detect  item and serving automatically.",
    images: {
      x1: tutorialReceipts1x,
      x2: tutorialReceipts2x,
      x3: tutorialReceipts3x,
    },
    contentWidth: 189,
    isBeta: true,
  },
  {
    title: "Food Package",
    description: "Scan any package to log items and serving sizes fast.",
    images: {
      x1: tutorialPackage1x,
      x2: tutorialPackage2x,
      x3: tutorialPackage3x,
    },
    contentWidth: 182,
  },
  {
    title: "Nutrition Facts",
    description: "Scan the label for instant, accurate nutrition info.",
    images: {
      x1: tutorialNutritionFact1x,
      x2: tutorialNutritionFact2x,
      x3: tutorialNutritionFact3x,
    },
    contentWidth: 189,
  },
]

const Tutorial = () => {
  useSetPageTitle("AI Scan Tutorial")
  const mealTypeQueryParams = useMealTypeQueryParams()
  const { updateIsTutorialShowed } = useIsTutorialShowed()
  const navigate = useNavigate()

  const { tutorialBackPath } = useTutorialBackPath()

  const onBack = () => {
    updateIsTutorialShowed(true)
    const backPath =
      tutorialBackPath ?? `${PATHS.app.meal.scan}${mealTypeQueryParams}`
    navigate(backPath, { replace: true })
  }

  const onStartUsingScan = () => {
    updateIsTutorialShowed(true)
    navigate(`${PATHS.app.meal.scan}${mealTypeQueryParams}`, { replace: true })
  }

  return (
    <TutorialWrapper>
      {/* Header */}
      <FlexBox $direction="column" style={{ marginBottom: 12 }}>
        <FlexBox
          style={{ height: 44, padding: "0px 19px" }}
          $justify="flex-start"
          $alignItems="center"
        >
          <BackButton onClick={onBack}>
            <WhiteCloseIcon />
          </BackButton>
        </FlexBox>
        <FlexBox $direction="column" style={{ padding: "0 24px 0 24px" }}>
          <PageTitle
            style={{ fontSize: "20px", fontWeight: 700, lineHeight: "1.2" }}
          >
            AI Scan
          </PageTitle>
          <PageSubtitle>
            Snap a photo, and we'll effortlessly handle the calorie and nutrient
            tracking for you.
          </PageSubtitle>
        </FlexBox>
      </FlexBox>

      {/* Feature Cards */}
      <FeatureCards>
        {features.map((feature, index) => (
          <FeatureCardItem
            key={index}
            title={feature.title}
            description={feature.description}
            images={feature.images}
            contentWidth={feature.contentWidth}
            isBeta={feature.isBeta}
          />
        ))}
      </FeatureCards>

      {/* Bottom Button */}
      <BottomCTA>
        <Button
          $type="primary"
          $theme="dark"
          $borderLess
          $textColor="#023648"
          style={{
            padding: "12px 24px",
            width: "100%",
          }}
          onClick={onStartUsingScan}
        >
          Start using AI Scan
          <ArrowLeft style={{ rotate: "-180deg" }} pathFill="#023648" />
        </Button>
      </BottomCTA>
    </TutorialWrapper>
  )
}

export default Tutorial
